// Canonical rows: what every uploaded file or Monday pull is mapped to before the merge.
//
// The Sources page reads a file, guesses which kind it is and which column holds which field
// (client/sources/detect.ts), lets Peter fix the guess, and stores the mapped rows. shared/ingest.ts merges the
// stored rows of every source into one Network. All values are strings; blank means unknown.

import type { SourceKind } from "./network";

export type CanonRow = Record<string, string>;

export interface FieldDef {
  key: string;
  label: string;
  /** Header names that mean this field, most specific first. Matched case-insensitively against trimmed headers. */
  hints: RegExp[];
}

const F = (key: string, label: string, ...hints: RegExp[]): FieldDef => ({ key, label, hints });

export const FIELDS: Record<SourceKind, FieldDef[]> = {
  crm: [
    F("name", "Name", /^name$/i, /^(full|contact|investor) name$/i, /^contact$/i, /^item( name)?$/i, /^person$/i),
    F("first", "First name", /^first( name)?$/i, /^given name$/i),
    F("last", "Last name", /^last( name)?$/i, /^(family name|surname)$/i),
    F("email", "Email", /^e-?mail( address)?$/i, /^(primary |work )?e-?mail/i, /e-?mail/i),
    F("phone", "Phone", /^(phone|mobile|cell)/i, /phone/i),
    F("company", "Company", /^(company|organi[sz]ation|firm|employer|account)( name)?$/i, /company/i),
    F("title", "Title", /^(job )?title$/i, /^(position|role)$/i),
    F("type", "Investor type", /^investor type$/i, /^(contact |investor )?(type|category|segment|classification)$/i, /type/i),
    F("sectors", "Sectors", /^sectors?$/i, /^(industr(y|ies)|focus|interests?|verticals?)$/i, /sector|industr/i),
    F("dealTypes", "Deal types", /^deal types?$/i, /^(structures?|deal structure)$/i),
    F("checkMin", "Check size min", /check.*min|min.*check|^min(imum)?( check| ticket)?$/i),
    F("checkMax", "Check size max", /check.*max|max.*check|^max(imum)?( check| ticket)?$/i),
    F("check", "Check size", /^(check|ticket)( size)?$/i, /check size|ticket/i),
    F("location", "Location (City, ST)", /^(location|city ?\/ ?state|city, ?state|metro|hometown)$/i, /location/i),
    F("city", "City", /^city$/i, /city/i),
    F("state", "State", /^(state|province|st)$/i, /^address.*region$/i),
    F("strength", "Relationship strength", /^relationship( strength)?$/i, /^(strength|rating|priority|stars?|tier|warmth)$/i, /relationship/i),
    F("howMet", "How we met", /how.*met|^(source|referr(al|ed by)|intro(duced)? by|origin)$/i),
    F("status", "Status", /^status$/i, /^stage$/i),
    F("notes", "Notes", /^notes?$/i, /^(comments?|description|updates?|background|bio)$/i, /notes?/i),
    F("lastContacted", "Last contacted", /last (contact(ed)?|touch|call|spoke|talked|interaction|meeting)/i),
    F("pastDeals", "Past deals", /past deals|deals w\/|prior deals/i),
    F("group", "Group", /^group$/i),
    F("itemId", "Item ID", /^item id$/i, /^(id|record id)$/i),
    F("linkedin", "LinkedIn URL", /linkedin/i),
  ],
  contacts: [
    F("name", "Name", /^name$/i, /^(display|full) name$/i),
    F("first", "First name", /^given name$/i, /^first( name)?$/i),
    F("last", "Last name", /^family name$/i, /^last( name)?$/i),
    F("email", "Email", /^e-?mail 1 - value$/i, /^e-?mail( address)?$/i, /^(primary )?e-?mail/i),
    F("email2", "Second email", /^e-?mail 2 - value$/i, /^(other|secondary|personal) e-?mail/i),
    F("phone", "Phone", /^phone 1 - value$/i, /^(mobile|phone)/i),
    F("company", "Company", /^organi[sz]ation 1 - name$/i, /^(company|organi[sz]ation)( name)?$/i),
    F("title", "Title", /^organi[sz]ation 1 - title$/i, /^(job )?title$/i),
    F("city", "City", /^address 1 - city$/i, /^city$/i),
    F("state", "State", /^address 1 - region$/i, /^(state|region)$/i),
    F("notes", "Notes", /^notes?$/i),
    F("linkedin", "LinkedIn URL", /linkedin/i, /^website 1 - value$/i),
  ],
  linkedin: [
    F("first", "First name", /^first name$/i),
    F("last", "Last name", /^last name$/i),
    F("email", "Email", /^email address$/i, /e-?mail/i),
    F("company", "Company", /^company$/i),
    F("title", "Position", /^position$/i, /^title$/i),
    F("linkedin", "Profile URL", /^url$/i, /profile/i),
    F("connectedOn", "Connected on", /^connected on$/i),
  ],
  conference: [
    F("name", "Attendee", /^(attendee|registrant|delegate)( name)?$/i, /^(full )?name$/i),
    F("first", "First name", /^first( name)?$/i),
    F("last", "Last name", /^last( name)?$/i),
    F("email", "Email", /e-?mail/i),
    F("company", "Company", /^(company|organi[sz]ation|firm)( name)?$/i),
    F("title", "Title", /^(job )?title$/i, /^position$/i),
    F("location", "City/State", /^(city ?\/ ?state|location|city, ?state)$/i),
    F("registration", "Registration type", /registration|ticket|attendee type|badge/i),
    F("interests", "Interests", /interest|focus|sector/i),
  ],
  touches: [
    F("date", "Date", /^date$/i, /date|^when$/i),
    F("email", "Email", /e-?mail/i),
    F("name", "Contact", /^(contact|contact_name|contact name|name|person|who)$/i),
    F("channel", "Channel", /^(channel|type|medium|via)$/i),
    F("direction", "Direction", /^direction$/i, /in\/?out|inbound|outbound/i),
    F("summary", "Summary", /^(summary|notes?|subject|body|message|description)$/i),
  ],
  deals: [
    F("dealId", "Deal ID", /^deal[_ ]?id$/i, /^id$/i),
    F("name", "Deal name", /^(name|deal|deal name|project)$/i),
    F("sectors", "Sectors", /sector|industr/i),
    F("structure", "Structure", /structure|deal type/i),
    F("raise", "Raise (USD)", /^raise|amount raised|size/i),
    F("launch", "Launch date", /launch|start|opened/i),
    F("close", "Close date", /^close|closed|end/i),
    F("sponsor", "Sponsor", /sponsor|gp|client/i),
  ],
  participation: [
    F("dealId", "Deal ID", /^deal[_ ]?id$/i, /^deal$/i),
    F("email", "Email", /e-?mail/i),
    F("name", "Contact", /^(contact|contact_name|contact name|name|investor)$/i),
    F("outcome", "Outcome", /outcome|result|status|response/i),
    F("amount", "Amount", /amount|commit|invested/i),
    F("date", "Date pitched", /pitched|date/i),
  ],
};

export const KIND_LABEL: Record<SourceKind, string> = {
  crm: "CRM board (Monday)",
  contacts: "Google Contacts",
  linkedin: "LinkedIn connections",
  conference: "Conference list",
  touches: "Touch log (calls, texts, emails)",
  deals: "Past deals",
  participation: "Who invested in past deals",
};

/** Fields without which a source can't be used. */
export const REQUIRED: Record<SourceKind, string[][]> = {
  // each inner list is "one of these"
  crm: [["name", "first", "last"]],
  contacts: [["name", "first", "last", "email"]],
  linkedin: [["first", "last"]],
  conference: [["name", "first", "last"]],
  touches: [["date"], ["email", "name"]],
  deals: [["dealId"], ["sectors"]],
  participation: [["dealId"], ["email", "name"]],
};
